import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { refreshBranding, POWERED, isLightHex } from "@/lib/branding";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Home, RotateCcw, Upload } from "lucide-react";

const card = "bg-white rounded-[16px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100";
const btnLine = "text-sm px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors";

const shrinkLogo = (file) => new Promise((resolve) => {
  createImageBitmap(file).then((bmp) => {
    const k = Math.min(1, 800 / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * k); c.height = Math.round(bmp.height * k);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    c.toBlob((b) => resolve(b || file), "image/png");
  }).catch(() => resolve(file));
});

const readLogo = async (file, cb) => {
  if (!file) return;
  const f = file.size > 1_400_000 ? await shrinkLogo(file) : file;
  const reader = new FileReader();
  reader.onload = () => cb(reader.result);
  reader.readAsDataURL(f);
};

function LogoBox({ b, size = "h-16 w-16", testid }) {
  return b.logo
    ? <img src={b.logo} alt="logótipo" className={`${size} rounded-xl object-contain bg-white border border-slate-200`} data-testid={testid} />
    : <div className={`${size} rounded-xl grid place-items-center text-white font-heading text-xl`} style={{ backgroundColor: b.color }}>{(b.name || "E").slice(0, 1)}</div>;
}

function Preview({ b }) {
  return (
    <div className={card}>
      <h3 className="font-heading text-lg text-ec-green mb-4">Pré-visualização</h3>
      <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="branding-preview">
        <div className={`px-4 py-3 flex items-center gap-2.5 ${b.header_dark ? "" : "bg-white border-b-2"}`} style={b.header_dark ? { backgroundColor: b.header_color || "#2B3A4A" } : { borderBottomColor: b.color }} data-testid="branding-preview-header">
          {b.logo ? <img src={b.logo} alt="" className="h-7 max-w-[90px] object-contain" /> : <Home size={18} className={b.header_dark ? "text-white" : "text-ec-green"} />}
          <span className={`font-heading text-lg ${b.header_dark ? "text-white" : "text-ec-green"}`} data-testid="branding-preview-portal">{b.portal_name || "Portal da Escritura"}</span>
        </div>
        <div className="p-5 bg-slate-50">
          <div className="text-xs text-slate-500">{b.name}</div>
          <div className="mt-1 text-sm text-slate-700">Olá. Esta é a sua página segura para enviar documentos.</div>
          <div className="mt-3 inline-block rounded-lg text-white text-sm font-semibold px-3 py-1.5" style={{ backgroundColor: b.dark_color || "#12332B", color: isLightHex(b.dark_color) ? "#1f2937" : "#fff" }}>Fotografar ou enviar PDF</div>
          <div className="mt-3 h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full w-2/3" style={{ backgroundColor: b.accent_color || b.dark_color || "#12332B" }} /></div>
        </div>
        {!b.white_label && <div className="text-[11px] text-slate-400 text-center py-2 bg-slate-50">{POWERED}</div>}
      </div>
      <p className="text-xs text-slate-500 mt-3">A cor principal aparece no cabeçalho. Na página do cliente usam-se a cor escura e a cor de destaque; os estados (a verificar, conferido, problema) têm sempre cores fixas.</p>
    </div>
  );
}

export function Marca() {
  const [b, setB] = useState(null);
  const fileRef = useRef();
  useEffect(() => { api.get("/agency/branding").then(({ data }) => setB(data)); }, []);
  const save = async () => {
    try {
      const { data } = await api.put("/agency/branding", { logo: b.logo, color: b.color, name: b.name, trade_name: b.trade_name_set ? b.trade_name : "", portal_name: b.portal_name || "", header_dark: !!b.header_dark, dark_color: b.dark_color, accent_color: b.accent_color || "" });
      setB(data); refreshBranding(); toast.success("Marca guardada");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  if (!b) return <div className={card}>A carregar…</div>;
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className={card}>
          <h3 className="font-heading text-lg text-ec-green mb-1">Marca da agência</h3>
          <p className="text-sm text-slate-500 mb-5">Logótipo, cor e nomes que os seus colaboradores e clientes veem.</p>
          <Label htmlFor="agency-name">Nome da agência</Label>
          <Input id="agency-name" value={b.name || ""} onChange={(e) => setB({ ...b, name: e.target.value })} className="mt-1.5 mb-5" data-testid="agency-name-input" />
          <Label htmlFor="trade-name">Nome comercial</Label>
          <Input id="trade-name" value={b.trade_name || ""} onChange={(e) => setB({ ...b, trade_name: e.target.value, trade_name_set: true })}
            className="mt-1.5" data-testid="trade-name-input" />
          <p className="text-xs text-slate-500 mt-1 mb-5">Na assinatura e no assunto das mensagens aos clientes (ex.: «Vasco Lopes · {b.trade_name || "KW Select Lumiar"}»).</p>
          <Label htmlFor="portal-name">Nome do portal</Label>
          <Input id="portal-name" value={b.portal_name || ""} placeholder="Portal da Escritura" onChange={(e) => setB({ ...b, portal_name: e.target.value })}
            className="mt-1.5 mb-5" data-testid="portal-name-input" />
          <Label>Logótipo</Label>
          <div className="flex items-center gap-4 mt-2">
            <LogoBox b={b} testid="branding-logo-preview" />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => readLogo(e.target.files?.[0], (logo) => setB({ ...b, logo }))} data-testid="branding-logo-input" />
            <button onClick={() => fileRef.current?.click()} className={btnLine} data-testid="branding-logo-btn">Escolher imagem</button>
            {b.logo && <button onClick={() => setB({ ...b, logo: null })} className={`${btnLine} text-red-600 hover:bg-red-50`} data-testid="branding-logo-remove">Remover</button>}
          </div>
          <Label className="block mt-6">Cor principal</Label>
          <div className="flex items-center gap-3 mt-2">
            <input type="color" value={b.color} onChange={(e) => setB({ ...b, color: e.target.value })} className="h-10 w-14 rounded-lg border border-slate-200 cursor-pointer" data-testid="branding-color-input" />
            <span className="font-mono text-sm text-slate-500">{b.color}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            {[["dark_color", "Cor escura", "Cartão do topo e botões principais da página do cliente."], ["accent_color", "Cor de destaque", "Barra de progresso, links e pormenores."]].map(([k, label, hint]) => (
              <div key={k}>
                <Label>{label}</Label>
                <div className="flex items-center gap-3 mt-2">
                  <input type="color" value={b[k] || b.dark_color || "#12332B"} onChange={(e) => setB({ ...b, [k]: e.target.value })} className="h-10 w-14 rounded-lg border border-slate-200 cursor-pointer" data-testid={`branding-${k}-input`} />
                  <span className="font-mono text-sm text-slate-500">{b[k] || "—"}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{hint}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between gap-3 mt-6 max-w-sm">
            <div><Label>Cabeçalho escuro</Label><p className="text-xs text-slate-500">Barra azul-marinho com o logótipo à esquerda.</p></div>
            <Switch checked={!!b.header_dark} onCheckedChange={(v) => setB({ ...b, header_dark: v })} data-testid="branding-header-dark" />
          </div>
          <button onClick={save} className="mt-6 bg-ec-green text-white px-4 py-2 rounded-lg hover:bg-ec-greenDark transition-colors" data-testid="save-branding">Guardar</button>
        </div>
        <Preview b={b} />
      </div>
      <EmailHero b={b} setB={setB} />
      <PlatformWhiteLabel />
    </div>
  );
}

// Faixa opcional do email de assinatura do CMI (desligada por defeito): imagem + frase; a frase lê-se mesmo com imagens bloqueadas.
function EmailHero({ b, setB }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/agency/branding", { email_hero_enabled: !!b.email_hero_enabled, email_hero_text: b.email_hero_text || "", email_hero_image: b.email_hero_image || null });
      setB(data); toast.success("Faixa do email guardada");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className={card} data-testid="email-hero-settings">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-lg text-ec-green mb-1">Faixa do email de assinatura</h3>
          <p className="text-sm text-slate-500">Imagem e frase por baixo do logótipo no email do CMI para assinar. Desligada, o email leva só o logótipo e o nome da agência.</p>
        </div>
        <Switch checked={!!b.email_hero_enabled} onCheckedChange={(v) => setB({ ...b, email_hero_enabled: v })} data-testid="email-hero-enabled" />
      </div>
      {b.email_hero_enabled && (
        <div className="mt-5 space-y-4">
          <div>
            <Label htmlFor="email-hero-text" className="block">Frase (opcional · a 2.ª linha fica a negrito)</Label>
            <textarea id="email-hero-text" rows={2} maxLength={120} value={b.email_hero_text || ""} onChange={(e) => setB({ ...b, email_hero_text: e.target.value })}
              placeholder={"Mais do que casas,\nrealizamos sonhos."} className="mt-1.5 w-full max-w-md rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="email-hero-text" />
          </div>
          <div>
            <Label className="block">Imagem (opcional)</Label>
            <div className="flex items-center gap-4 mt-2">
              {b.email_hero_image ? <img src={b.email_hero_image} alt="" className="h-16 w-28 object-cover rounded-lg border border-slate-200" data-testid="email-hero-preview" /> : <div className="h-16 w-28 rounded-lg border border-dashed border-slate-300" />}
              <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => readLogo(e.target.files?.[0], (img) => setB({ ...b, email_hero_image: img }))} data-testid="email-hero-input" />
              <button onClick={() => ref.current?.click()} className={btnLine} data-testid="email-hero-btn">Escolher imagem</button>
              {b.email_hero_image && <button onClick={() => setB({ ...b, email_hero_image: null })} className={`${btnLine} text-red-600 hover:bg-red-50`} data-testid="email-hero-remove">Remover</button>}
            </div>
          </div>
        </div>
      )}
      <button onClick={save} disabled={busy} className="mt-5 bg-ec-green text-white px-4 py-2 rounded-lg hover:bg-ec-greenDark transition-colors disabled:opacity-60" data-testid="save-email-hero">Guardar</button>
    </div>
  );
}

function PlatformWhiteLabel() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  useEffect(() => { if (user?.is_platform_admin) api.get("/platform/agencies").then(({ data }) => setRows(data)); }, [user]);
  if (!user?.is_platform_admin) return null;
  const toggle = async (a, v) => {
    try {
      await api.put(`/platform/agencies/${a.id}/white-label`, { white_label: v });
      setRows(rows.map((r) => (r.id === a.id ? { ...r, white_label: v } : r))); refreshBranding();
      toast.success(v ? `Rodapé retirado: ${a.name}` : `Rodapé reposto: ${a.name}`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className={card} data-testid="platform-white-label">
      <h3 className="font-heading text-lg text-ec-green mb-1">Administração da plataforma · white-label</h3>
      <p className="text-sm text-slate-500 mb-4">Só visível para administradores da plataforma. Ligado = sem o rodapé «{POWERED}» em todas as vistas da agência.</p>
      {rows.map((a) => (
        <div key={a.id} className="flex items-center justify-between py-2.5 border-t border-slate-100 first:border-t-0">
          <span className="text-[15px] text-ec-green">{a.name}</span>
          <Switch checked={a.white_label} onCheckedChange={(v) => toggle(a, v)} data-testid={`white-label-${a.id}`} />
        </div>
      ))}
    </div>
  );
}

export function DemoBranding() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const timer = useRef();
  useEffect(() => { api.get("/agency/branding").then(({ data }) => setB(data)); }, []);
  const onColor = (color) => { setB({ ...b, color }); clearTimeout(timer.current); timer.current = setTimeout(() => apply({ color }), 600); };
  const apply = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.post("/agency/branding/demo", patch);
      setB(data); refreshBranding(); toast.success("Marca da demonstração aplicada");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const restore = async () => {
    try { const { data } = await api.post("/agency/branding/demo/restore"); setB(data); refreshBranding(); toast.success("Marca original reposta"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  if (!b) return null;
  return (
    <div className={card + " space-y-4"} data-testid="demo-branding">
      <div>
        <div className="font-heading text-xl text-ec-green">Marca da demonstração</div>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">Mostre a aplicação com a marca da agência que está a visitar. Carregue o logótipo e escolha a cor: aplica-se logo, sem mexer nos processos.</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <LogoBox b={b} size="h-12 w-12" />
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => readLogo(e.target.files?.[0], (logo) => apply({ logo }))} data-testid="demo-logo-input" />
        <button onClick={() => fileRef.current?.click()} disabled={busy} className={`${btnLine} inline-flex items-center gap-1.5`} data-testid="demo-logo-btn"><Upload size={14} /> Carregar logótipo</button>
        <input type="color" value={b.color} onChange={(e) => onColor(e.target.value)}
          className="h-10 w-14 rounded-lg border border-slate-200 cursor-pointer" data-testid="demo-color-input" />
        <Input value={b.name || ""} onChange={(e) => setB({ ...b, name: e.target.value })} onBlur={(e) => e.target.value.trim().length >= 2 && apply({ name: e.target.value })}
          className="w-60" placeholder="Nome da agência" data-testid="demo-name-input" />
        {b.demo_active && <button onClick={restore} className={`${btnLine} inline-flex items-center gap-1.5`} data-testid="demo-branding-restore"><RotateCcw size={14} /> Repor marca original</button>}
      </div>
    </div>
  );
}
